import React, { useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { LayoutDashboard, MessageSquare, Users, Plus, Menu } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import { SidebarNavContent } from "@/components/layout/Sidebar";

/**
 * Mobile bottom navigation. Hidden on `md` and above (desktop uses the
 * sidebar). Five slots: Overview · Comments · Leads · Compose · Menu.
 * "Menu" opens a Sheet with the full sidebar nav (roles-aware).
 */
export const BottomNav: React.FC = () => {
  const [open, setOpen] = useState(false);
  const loc = useLocation();

  const isCompose = loc.pathname.startsWith("/upload");

  const items: Array<{
    to?: string;
    label: string;
    icon: any;
    tid: string;
    highlight?: boolean;
    onClick?: () => void;
    active?: boolean;
  }> = [
    { to: "/dashboard", label: "Home", icon: LayoutDashboard, tid: "bottom-nav-home" },
    { to: "/comments", label: "Comments", icon: MessageSquare, tid: "bottom-nav-comments" },
    { to: "/upload", label: "Compose", icon: Plus, tid: "bottom-nav-compose", highlight: true, active: isCompose },
    { to: "/leads", label: "Leads", icon: Users, tid: "bottom-nav-leads" },
    { to: undefined, label: "Menu", icon: Menu, tid: "bottom-nav-menu", onClick: () => setOpen(true) },
  ];

  return (
    <>
      <nav
        data-testid="bottom-nav"
        className="md:hidden fixed bottom-0 inset-x-0 z-40 bg-[color:var(--sg-card)]/95 backdrop-blur-md border-t border-[color:var(--sg-border)] pb-[max(0.25rem,env(safe-area-inset-bottom))]"
        aria-label="Primary mobile navigation"
      >
        <div className="grid grid-cols-5 h-14">
          {items.map((it) => {
            const Icon = it.icon;
            if (it.highlight && it.to) {
              return (
                <NavLink
                  key={it.tid}
                  to={it.to}
                  data-testid={it.tid}
                  className={({ isActive }) => `flex items-center justify-center relative ${isActive ? "" : ""}`}
                >
                  <div className="absolute -top-4 h-11 w-11 rounded-full bg-[color:var(--sb-saffron,#F97316)] text-white flex items-center justify-center shadow-lg shadow-[color:var(--sb-saffron,#F97316)]/30 ring-4 ring-[color:var(--sg-card)]">
                    <Icon className="h-5 w-5" strokeWidth={2.5} />
                  </div>
                  <span className="text-[10px] font-medium text-[color:var(--sg-muted)] mt-6">{it.label}</span>
                </NavLink>
              );
            }
            if (it.onClick) {
              return (
                <Sheet key={it.tid} open={open} onOpenChange={setOpen}>
                  <SheetTrigger asChild>
                    <button
                      data-testid={it.tid}
                      onClick={it.onClick}
                      className="flex flex-col items-center justify-center gap-0.5 text-[color:var(--sg-muted)] hover:text-[color:var(--sg-fg)] transition-colors"
                    >
                      <Icon className="h-5 w-5" strokeWidth={2} />
                      <span className="text-[10px] font-medium">{it.label}</span>
                    </button>
                  </SheetTrigger>
                  <SheetContent
                    side="right"
                    className="p-0 w-72 max-w-[85vw] h-dvh flex flex-col"
                    data-testid="bottom-nav-menu-drawer"
                  >
                    <VisuallyHidden>
                      <SheetTitle>Main navigation</SheetTitle>
                      <SheetDescription>SociBaba dashboard menu</SheetDescription>
                    </VisuallyHidden>
                    <div
                      data-testid="bottom-nav-menu-scroll"
                      className="flex-1 min-h-0 overflow-y-auto overscroll-contain"
                    >
                      <SidebarNavContent
                        onNavigate={() => setOpen(false)}
                        showWallet
                      />
                    </div>
                  </SheetContent>
                </Sheet>
              );
            }
            return (
              <NavLink
                key={it.tid}
                to={it.to!}
                data-testid={it.tid}
                className={({ isActive }) =>
                  `relative flex flex-col items-center justify-center gap-0.5 transition-colors ${
                    isActive
                      ? "text-[color:var(--sg-fg)]"
                      : "text-[color:var(--sg-muted)] hover:text-[color:var(--sg-fg)]"
                  }`
                }
              >
                {({ isActive }) => (
                  <>
                    <Icon className="h-5 w-5" strokeWidth={isActive ? 2.5 : 2} />
                    <span className={`text-[10px] ${isActive ? "font-semibold" : "font-medium"}`}>{it.label}</span>
                    {isActive && (
                      <span className="absolute top-0 h-0.5 w-8 bg-[color:var(--sg-fg)] rounded-b-full" />
                    )}
                  </>
                )}
              </NavLink>
            );
          })}
        </div>
      </nav>
      {/* Content spacer so the last row isn't hidden behind the fixed bar */}
      <div className="md:hidden h-16" aria-hidden />
    </>
  );
};
